/**
 * Live Shift of a line: what the line is doing now and who stops it.
 *
 * The line counts at its output machine: the line stands while the output
 * machine does not run, and every minute it stands is traced back along the
 * line to the machine that caused it (lineFlow.ts). Shown: the line now, its
 * output against the target, how long it ran and what the stops cost, the
 * machines that stopped it most since the shift start, and the operating
 * timeline of all machines in line order. The machines themselves are in the
 * line card of the header. How it is calculated is in the help.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { cx } from "@digitalisation/enlixy-comp";
import { useThemeClasses } from "../../providers/ThemeProvider";
import type { LiveShiftData } from "../../hooks/useLiveShiftData";
import type { MachineSummaryRow, StateCategory, StateInterval } from "../../services/ShiftAnalysisService";
import { useSAPDataStore } from "../../store/SAPDataStore";
import Panel from "../shiftAnalysis/Panel";
import StatusTimeline from "../shiftAnalysis/StatusTimeline";
import { stationAxisLabel, type LineStation } from "../line/lineStations";
import { useLineConfigInfo } from "../line/useLineData";
import { LiveInsights } from "./LiveInsights";
import {
  analyzeLineFlow,
  buildIntervals,
  machineOf,
  shiftInstant,
  wallClockText,
  type CauseKey,
  type FlowState,
  type Interval,
  type LineFlow,
} from "./lineFlow";
import {
  formatHms,
  isFresh,
  lineInsights,
  lineLiveOutput,
  liveTargets,
  liveValues,
  localTime,
  lostOutput,
  outputOutlook,
  shiftClock,
  statusOf,
  type MachineRef,
} from "./liveShiftInsights";
import type { MachineLive } from "./useLineLiveShift";
import { useLineStatusHistory, type StatusHistory } from "./useLineStatusHistory";

interface LineLiveShiftProps {
  lineId: string;
  stations: LineStation[];
  live: Map<string, MachineLive>;
  loading: boolean;
}

/** Causes listed in "Who stops the line". */
const MAX_CAUSES = 5;

const num = (v: number, decimals = 0) => v.toLocaleString(undefined, { maximumFractionDigits: decimals });

/** HH:MM of the edge's shift times (local wall-clock times in UTC fields). */
const wallClock = (iso: string) => {
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`
    : "--";
};

/** The shift-analysis timeline's state for a flow state. */
const TIMELINE_STATE: Record<FlowState, StateCategory> = {
  running: "running",
  own: "stopped",
  ownWait: "waiting_int",
  starved: "waiting_ext",
  blocked: "waiting_ext",
  setup: "setup",
  standby: "standby",
};

const card = cx(
  "rounded-2xl",
  "bg-white dark:bg-slate-800",
  "border border-gray-100 dark:border-slate-700",
  "shadow-sm dark:shadow-lg dark:shadow-black/20",
);

/** Re-render every `ms`, so durations "since" keep counting between live messages. */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const LineLiveShift: React.FC<LineLiveShiftProps> = ({ lineId, stations, live, loading }) => {
  const { t } = useTranslation();
  const tc = useThemeClasses();
  const navigate = useNavigate();
  const kpiTargetIndex = useSAPDataStore((s) => s.kpiTargetIndex);
  const { contains } = useLineConfigInfo(lineId);
  const nowMs = useNow(10_000);

  // the shift: that of the output machine, else the latest of the line
  const reference = useMemo(() => {
    const fresh = stations
      .map((s) => ({ s, shift: live.get(s.sn)?.shift }))
      .filter((x) => x.shift && isFresh(x.shift.timestamp));
    return (
      fresh.find((x) => x.s.isOutput)?.shift ??
      fresh.map((x) => x.shift!).sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0]
    );
  }, [stations, live]);
  const startMs = reference ? shiftInstant(reference.shift.start) : null;

  // all machines of the line, so a poll that misses one does not restart the history
  const historySns = useMemo(() => stations.map((s) => s.sn), [stations]);
  const history = useLineStatusHistory(historySns, startMs);

  const label = useMemo(() => new Map(stations.map((s) => [s.sn, stationAxisLabel(s)])), [stations]);
  const causeRef = useMemo(
    () =>
      (key: CauseKey): MachineRef => {
        const sn = machineOf(key);
        if (sn) return { sn, label: label.get(sn) ?? sn };
        return {
          label: t(
            key === "upstream"
              ? "lineLive.causeUpstream"
              : key === "downstream"
                ? "lineLive.causeDownstream"
                : "lineLive.causeUnknown",
          ),
        };
      },
    [label, t],
  );

  const view = useMemo(() => {
    const clock = reference ? shiftClock(reference.shift.start, reference.shift.end) : null;

    // every machine's statuses since the shift start (history + its last transition)
    const lanes = new Map<string, Interval[]>();
    if (startMs) {
      for (const s of stations) {
        const ivs = buildIntervals(history.transitions.get(s.sn) ?? [], live.get(s.sn)?.transition?.transition, startMs, nowMs);
        if (ivs.length) lanes.set(s.sn, ivs);
      }
    }
    const flow =
      startMs && history.ready && !history.failed
        ? analyzeLineFlow(
            stations.map((s) => ({ sn: s.sn, stage: s.stage, isOutput: s.isOutput })),
            lanes,
            startMs,
            nowMs,
          )
        : null;

    const machines = stations.map((s) => {
      const m = live.get(s.sn);
      return {
        machine: { label: stationAxisLabel(s), sn: s.sn },
        values: liveValues(m?.shift, m?.topStops),
        targets: liveTargets(kpiTargetIndex, { machineSn: s.sn, lineBk: lineId, machineType: s.type }, m?.shift?.kpis.target),
        fresh: isFresh(m?.shift?.timestamp, nowMs),
      };
    });

    const outputs = stations.filter((s) => s.isOutput);
    const lineOutput = lineLiveOutput(
      outputs.map((s) => ({
        sn: s.sn,
        shift: live.get(s.sn)?.shift,
        fresh: isFresh(live.get(s.sn)?.shift?.timestamp, nowMs),
        target: machines[stations.indexOf(s)].targets.output,
      })),
      contains,
      kpiTargetIndex.get(`line:${lineId}`)?.targets.output,
    );
    const outlook = outputOutlook(lineOutput.output, lineOutput.target?.value, clock);
    const lost = flow
      ? lostOutput(
          flow.lineDownSeconds,
          outputs.map((s) => ({ sn: s.sn, nominalSpeed: live.get(s.sn)?.shift?.kpis.nominal_speed ?? null })),
          contains,
        )
      : null;

    // the shift-analysis timeline, in local wall-clock text
    const stateLanes = new Map<string, StateInterval[]>(
      [...lanes].map(([sn, ivs]) => [
        sn,
        ivs.map((iv) => ({
          machine_sn: sn,
          machine_name: label.get(sn) ?? sn,
          state_start: wallClockText(iv.start),
          state_end: wallClockText(iv.end),
          duration_s: (iv.end - iv.start) / 1000,
          state_text: iv.text,
          state_category: TIMELINE_STATE[iv.state],
        })),
      ]),
    );
    const timelineMachines: MachineSummaryRow[] = stations.map((s) => ({
      machine_sn: s.sn,
      machine_name: stationAxisLabel(s),
      output: 0,
      waste: 0,
      reject_rate: 0,
      efficiency: 0,
      utilization: 0,
      downtime_ms: 0,
      mtbf: 0,
      status: "no_data",
    }));

    return {
      clock,
      flow,
      lineOutput,
      outlook,
      lost,
      stateLanes,
      timelineMachines,
      insights: lineInsights(flow, causeRef, outlook, machines),
    };
  }, [reference, startMs, stations, history, live, nowMs, contains, kpiTargetIndex, lineId, label, causeRef]);

  if (loading && live.size === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full" />
        <span className="text-gray-500 font-medium mt-4">{t("liveShiftConnecting")}</span>
      </div>
    );
  }
  if (!reference) {
    return <p className={cx(tc.textSecondary, "p-6 text-sm")}>{t("lineLive.allStale")}</p>;
  }

  const { clock, flow, lineOutput, outlook } = view;
  const outputs = stations.filter((s) => s.isOutput);
  const elapsedSeconds = startMs ? Math.max(0, (nowMs - startMs) / 1000) : 0;
  const ranPct = flow && elapsedSeconds > 0 ? ((elapsedSeconds - flow.lineDownSeconds) / elapsedSeconds) * 100 : null;
  const openMachine = (sn: string) => navigate(`/machine/${sn}`, { state: { lineId } });

  return (
    <div className="p-4 md:p-6 space-y-5 min-h-full">
      {/* The shift and the line now, as the banner of a machine's Live Shift */}
      <LineBanner
        reference={reference}
        progressPct={clock ? clock.fraction * 100 : 0}
        flow={flow}
        history={history}
        causeRef={causeRef}
        nowMs={nowMs}
        t={t}
      />

      {/* Line KPIs: measured at the output machine */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile title={t("lineLive.lineOutput")} tc={tc}>
          {lineOutput.output === null ? (
            <span className={cx(tc.textMuted, "text-sm")}>{t("lineLive.noOutputData")}</span>
          ) : (
            <>
              <span className="text-2xl font-black tabular-nums">{num(lineOutput.output)}</span>
              {lineOutput.target && (
                <span className={cx(tc.textMuted, "text-sm ml-1")}>/ {num(lineOutput.target.value)}</span>
              )}
              <div className={cx(tc.textMuted, "text-[0.7rem] mt-1")}>
                {outputs
                  .map((s) => {
                    const good = live.get(s.sn)?.shift?.kpis.good_products;
                    const factor = contains[s.sn] > 0 ? contains[s.sn] : 1;
                    const machine = stationAxisLabel(s);
                    return typeof good === "number"
                      ? factor > 1
                        ? t("lineLive.outputBasisContains", { output: num(good), contains: num(factor), machine })
                        : t("lineLive.outputBasis", { machine })
                      : machine;
                  })
                  .join(" + ")}
              </div>
              {outlook && (
                <div
                  className={cx(
                    "text-xs mt-1 font-semibold",
                    outlook.pacePct === null
                      ? tc.textMuted
                      : statusOf(outlook.pacePct, 100, "min") === "ok"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : statusOf(outlook.pacePct, 100, "min") === "warn"
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-red-600 dark:text-red-400",
                  )}
                >
                  {t("liveTargets.projected")}: {outlook.projected === null ? "--" : num(outlook.projected)}
                </div>
              )}
            </>
          )}
        </Tile>
        <Tile title={t("lineLive.lineRan")} tc={tc}>
          <span className="text-2xl font-black tabular-nums">{ranPct === null ? "--" : `${num(ranPct, 1)}%`}</span>
          <div className={cx(tc.textMuted, "text-[0.7rem] mt-1")}>{t("lineLive.sinceStart")}</div>
        </Tile>
        <Tile title={t("lineLive.lineStops")} tc={tc}>
          <span className="text-2xl font-black tabular-nums">{flow ? num(flow.lineStops) : "--"}</span>
          {flow && (
            <span className={cx(tc.textMuted, "text-sm ml-2 tabular-nums")}>{formatHms(flow.lineDownSeconds)}</span>
          )}
          <div className={cx(tc.textMuted, "text-[0.7rem] mt-1")}>{t("lineLive.sinceStart")}</div>
        </Tile>
        <Tile title={t("lineLive.lostOutput")} tc={tc}>
          <span className="text-2xl font-black tabular-nums">
            {view.lost === null ? "--" : `≈ ${num(view.lost)}`}
          </span>
          <div className={cx(tc.textMuted, "text-[0.7rem] mt-1")}>{t("lineLive.atNominalSpeed")}</div>
        </Tile>
      </div>

      {/* Who stops the line, and the insights */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7">
          <Panel label={t("lineLive.whoStops")} meta={t("lineLive.sinceStart")} accent="#ef4444">
            <WhoStops flow={flow} history={history} causeRef={causeRef} onOpen={openMachine} t={t} tc={tc} />
          </Panel>
        </div>
        <LiveInsights insights={view.insights} className="col-span-12 lg:col-span-5" />
      </div>

      {/* What every machine did since the shift start, in line order; once the
          shift's statuses are read (before that only each machine's last one is known) */}
      {history.ready && !history.failed && view.stateLanes.size > 0 && (
        <StatusTimeline stateLanes={view.stateLanes} machines={view.timelineMachines} onPin={openMachine} />
      )}
    </div>
  );
};

const Tile: React.FC<{ title: string; tc: ReturnType<typeof useThemeClasses>; children: React.ReactNode }> = ({
  title,
  tc,
  children,
}) => (
  <div className={cx(card, "p-4")}>
    <div className={cx(tc.textMuted, "text-xs font-semibold uppercase tracking-wide mb-2")}>{title}</div>
    <div className={tc.textPrimary}>{children}</div>
  </div>
);

/** Local HH:MM:SS of an instant, as the machine banner shows its last update. */
const clockSeconds = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return Number.isFinite(d.getTime()) ? `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}` : "--";
};

/**
 * The shift and the line now, laid out like the banner of a machine's Live
 * Shift: the status dot and pill say whether the line runs or since when it
 * stands, and the cause next to it who stops it.
 */
const LineBanner: React.FC<{
  reference: LiveShiftData;
  progressPct: number;
  flow: LineFlow | null;
  history: StatusHistory;
  causeRef: (key: CauseKey) => MachineRef;
  nowMs: number;
  t: TFunction;
}> = ({ reference, progressPct, flow, history, causeRef, nowMs, t }) => {
  const { shift } = reference;
  const down = flow?.current.down ?? false;
  return (
    <div
      className={cx(
        "rounded-2xl p-4 md:p-5 relative overflow-hidden",
        "bg-gradient-to-r from-[#025a94] to-[#0284c7]",
        "shadow-lg shadow-blue-900/20",
      )}
    >
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-10" aria-hidden="true">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/20 -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-white/10 translate-y-1/2 -translate-x-1/4" />
      </div>

      <div className="relative flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className={cx(
                "w-3 h-3 rounded-full shadow-lg",
                !flow
                  ? "bg-white/50"
                  : down
                    ? "bg-amber-400 shadow-amber-400/50"
                    : "bg-green-400 animate-pulse shadow-green-400/50",
              )}
            />
            <span className="text-white font-bold text-lg">{shift.designation}</span>
          </div>
          <div className="hidden md:flex items-center gap-2 text-white/70 text-sm">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {wallClock(shift.start)}–{wallClock(shift.end)}
          </div>
          {flow ? (
            <div
              className={cx(
                "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold",
                down ? "bg-amber-400/20 text-amber-200" : "bg-emerald-400/20 text-emerald-200",
              )}
            >
              {down ? (
                <span aria-hidden="true">⏸</span>
              ) : (
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
              )}
              <span>
                {down
                  ? t("lineLive.lineDown", {
                      duration: formatHms((nowMs - (flow.current.since ?? nowMs)) / 1000),
                    })
                  : t("lineLive.lineRunning")}
              </span>
              {down && flow.current.since && (
                <span className="text-white/40 font-normal ml-1">
                  {localTime(new Date(flow.current.since).toISOString())}
                </span>
              )}
            </div>
          ) : (
            <span className="text-white/60 text-xs">
              {history.failed ? t("lineLive.noHistory") : t("lineLive.historyLoading")}
            </span>
          )}
          {down && flow?.current.cause && (
            <span className="text-white/90 text-sm min-w-0 truncate">
              {t("lineLive.causedBy", {
                cause: causeRef(flow.current.cause).label,
                reason: flow.current.reason ? ` · ${flow.current.reason}` : "",
              })}
            </span>
          )}
        </div>
        <div className="text-right text-white/80 text-xs">
          <div className="font-medium">{new Date(shift.date).toLocaleDateString()}</div>
          <div className="text-white/50 mt-0.5">{t("lineLive.updated", { time: clockSeconds(reference.timestamp) })}</div>
        </div>
      </div>

      {/* Shift time progress bar */}
      <div className="relative mt-4">
        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-green-400 to-emerald-300 rounded-full transition-all duration-1000"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="flex justify-between mt-1.5 text-[0.65rem] text-white/50">
          <span>{t("lineLive.shiftStart")}</span>
          <span>{t("lineLive.elapsed", { pct: num(progressPct) })}</span>
          <span>{t("lineLive.shiftEnd")}</span>
        </div>
      </div>
    </div>
  );
};

/** The causes of the line's stops since the shift start, largest first. */
const WhoStops: React.FC<{
  flow: ReturnType<typeof analyzeLineFlow>;
  history: ReturnType<typeof useLineStatusHistory>;
  causeRef: (key: CauseKey) => MachineRef;
  onOpen: (sn: string) => void;
  t: TFunction;
  tc: ReturnType<typeof useThemeClasses>;
}> = ({ flow, history, causeRef, onOpen, t, tc }) => {
  if (!flow) {
    return (
      <p className={cx(tc.textMuted, "text-sm")}>
        {history.failed ? t("lineLive.noHistory") : t("lineLive.historyLoading")}
      </p>
    );
  }
  const causes = flow.causes.filter((c) => c.lineDownSeconds > 0).slice(0, MAX_CAUSES);
  if (causes.length === 0) return <p className={cx(tc.textMuted, "text-sm")}>{t("lineLive.noStops")}</p>;
  const max = causes[0].lineDownSeconds;
  return (
    <ol className="space-y-3">
      {causes.map((c, idx) => {
        const ref = causeRef(c.key);
        const share = flow.lineDownSeconds > 0 ? (c.lineDownSeconds / flow.lineDownSeconds) * 100 : 0;
        const row = (
          <div className="w-full">
            <div className="flex items-baseline justify-between gap-3">
              <span className={cx(tc.textPrimary, "text-sm font-semibold truncate")}>
                <span
                  className={cx(
                    "inline-flex h-5 w-5 mr-2 items-center justify-center rounded-full text-[0.65rem] font-bold",
                    idx === 0
                      ? "bg-red-600 text-white"
                      : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
                  )}
                >
                  {idx + 1}
                </span>
                {ref.label}
              </span>
              <span className={cx(tc.textPrimary, "text-sm font-bold tabular-nums flex-shrink-0")}>
                {formatHms(c.lineDownSeconds)}
                <span className={cx(tc.textMuted, "font-normal ml-1")}>({num(share)}%)</span>
              </span>
            </div>
            <div className="mt-1.5 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={cx("h-full rounded-full", idx === 0 ? "bg-red-500" : "bg-red-300 dark:bg-red-400/70")}
                style={{ width: `${(c.lineDownSeconds / max) * 100}%` }}
              />
            </div>
            <div className={cx(tc.textMuted, "text-[0.7rem] mt-1 truncate")}>
              {[
                c.reasons[0]?.text,
                c.waitingCausedSeconds > 0
                  ? t("lineLive.waitingCaused", { duration: formatHms(c.waitingCausedSeconds) })
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        );
        return (
          <li key={c.key}>
            {ref.sn ? (
              <button
                type="button"
                onClick={() => onOpen(ref.sn!)}
                className="w-full text-left rounded-lg -mx-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-slate-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {row}
              </button>
            ) : (
              row
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default LineLiveShift;
